import { useEffect, useState } from 'react';

import { Alert, Box, Container } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { requestAll } from '../../../apis/config';
import { userApis } from '../../../apis/userApis';
import AlertMessage from '../../../components/elements/alert-message/AlertMessage';
import Loading from '../../../components/elements/loading/Loading';
import Text from '../../../components/elements/text/Text';
import Title from '../../../components/elements/title/Title';
import FA2Section from '../../../components/page-sections/2fa/FA2Section';
import SocialCards from '../../../components/page-sections/social-signin/SocialCards';
import DeleteButton from '../../../components/sections/delete/DeleteButton';
import TitlePanel from '../../../components/sections/title-panel/TitlePanel';
import { useUser } from '../../../contexts/UserContext';
import { handleErrors } from '../../../helpers';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { sentenceCase } from '../../../utils/utils';

// Page
const UserAccountSettingsPage = () => {
  const { t, i18n } = useTranslation();

  const { keycloak } = useKeycloak();
  const { user, refreshUser } = useUser();

  const [userProviders, setUserProviders] = useState(null);
  const [areProjects, setAreProjects] = useState(false);

  const [requestError, setRequestError] = useState(false);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const { showToast } = useToast();

  const fetchData = async (user) => {
    setLoading(true);
    try {
      const [userProvidersRequest, getProjectsRequest] = await requestAll([
        userApis.getUserProviders(user.id, window.location.href),
        userApis.getUserProjects(user.id),
      ]);

      setUserProviders(userProvidersRequest.data);
      setAreProjects(!getProjectsRequest.data.empty);
    } catch (error) {
      handleErrors(navigate, error, showToast);
      setRequestError(true);
    } finally {
      setLoading(false);
    }
  };

  // component events
  const toggle2FA = async () => {
    const otpState = user.otp;

    try {
      !otpState
        ? await userApis.enableUser2FA(user.id)
        : await userApis.disableUser2FA(user.id);
    } catch {
      showToast(
        TOAST_TYPE.ERROR,
        otpState
          ? t('enabled2FAError', { ns: 'notifications' })
          : t('disabled2FAError', { ns: 'notifications' })
      );
    } finally {
      await keycloak.updateToken(1800);
      if (otpState) {
        await refreshUser();
      } else {
        keycloak.login({ locale: i18n.language });
      }
    }
  };

  const onConnect = (provider, url) => {
    window.location.href = url;
  };

  const onDisconnect = async (provider) => {
    try {
      await userApis.deleteUserProvider(user.id, provider);
      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('deleteSuccess', {
            ns: 'notifications',
            text: t('socialProviders', { ns: 'sectionTitles' }),
          })
        )
      );
    } catch {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('deleteError', {
            ns: 'notifications',
            text: t('socialProviders', { ns: 'sectionTitles' }),
          })
        )
      );
    } finally {
      fetchData(user);
    }
  };

  const onDelete = async () => {
    try {
      await userApis.deleteUser(user.id);
      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('deleteSuccess', {
            ns: 'notifications',
            text: t('account', { ns: 'user' }),
          })
        )
      );

      setTimeout(() => {
        keycloak.logout({
          redirectUri: import.meta.env.VITE_APP_KANDASEARCH_HOME_PAGE,
        });
      }, 2000);
    } catch {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('deleteError', {
            ns: 'notifications',
            text: t('account', { ns: 'user' }),
          })
        )
      );
    }
  };

  useEffect(() => {
    if (!user) return;

    fetchData(user);
  }, [user]);

  if (loading) return <Loading />;

  if (requestError)
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="error">
          {sentenceCase(
            t('loading', {
              ns: 'errors',
              content: t('account', { ns: 'titles' }),
            })
          )}
        </Alert>
      </Box>
    );

  return (
    <Container>
      <TitlePanel title={t('account', { ns: 'titles' })} />
      {user && <FA2Section enabled={user.otp} toggle2FA={toggle2FA} />}
      {userProviders && (
        <SocialCards
          providerData={userProviders}
          handleConnect={onConnect}
          handleDisconnect={onDisconnect}
        />
      )}
      {user && (
        <>
          <Title
            subTitle
            text={t('accountDeletion', { ns: 'sectionTitles' })}
          />
          <Text>{t('accountDeletionWarning', { ns: 'descriptions' })}</Text>
          {areProjects ? (
            <AlertMessage
              severity="info"
              message={t('removeProjectBeforeDeleteAccount', {
                ns: 'descriptions',
              })}
            />
          ) : (
            <DeleteButton
              buttonProps={{ sx: { mt: 2 } }}
              buttonText={t('deleteAccount', { ns: 'buttons' })}
              onDelete={onDelete}
              validation={user.login}
              title={t('deleteAccount', { ns: 'buttons' })}
              description={t('areYouSure', {
                ns: 'descriptions',
                text: t('user', { ns: 'user' }),
              })}
            />
          )}
        </>
      )}
    </Container>
  );
};

export default UserAccountSettingsPage;
